import { clsx } from 'clsx'
import NextLink from 'next/link'
import { type FC, type ReactNode, useRef } from 'react'
import { useIntl } from 'react-intl'

import {
  type PimProductV11,
  PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../../../../global-config'
import { priceGetFormattedPrice } from '../../../../../price'
import { urlResolverGetPathForPimProduct } from '../../../../../url-handling'
import { productAccessorGetDataNoSnippetAttributeForInsurancePrescription } from '../../../../model/productAccessor/productAccessorGetDataNoSnippetAttributeForInsurancePrescription'
import { type SelectionEntry } from '../../../product-page-context/AttributeGroup'
import { bonusEnabledPrescriptionTypes } from '../../../ProductBuyBox/models/bonusEnabledPrescriptionTypes'
import { ProductDiscountBadge } from '../../../ProductBuyBox/ProductDiscountBadge'
import { ProductFormattedUnitPrice } from '../../../ProductFormattedUnitPrice'
import { useProductPageContext } from '../../../productPageContext'
import { useMobileScrollAttributeIntoViewOnMount } from '../useMobileScrollAttributeIntoViewOnMount'
import { type ScrollToElement } from '../useScrollElementIntoView'

import { ProductAttributeTileStrikeThroughPrice } from './ProductAttributeTileStrikeThroughPrice'
import { useProductAttributeTileWithPriceData } from './useProductAttributeTileWithPriceData'

type ProductAttributeTileWithPriceProps = {
  attributeDescription: ReactNode | string
  candidateVariant: PimProductV11
  className?: string
  hasAnyBonuses?: boolean
  hasAnyDiscounts?: boolean
  isVariantSelected: boolean
  onScrollIntoView?: ScrollToElement
  selectionEntry: SelectionEntry
  size: 'large' | 'small'
}

const classNames = {
  getAttributeDescriptionClass: ({
    isVariantNotAvailable,
    isVariantSelected,
    showBonusPrice,
  }: {
    isVariantNotAvailable: boolean
    isVariantSelected: boolean
    showBonusPrice: boolean
  }): string =>
    clsx(
      'w-full text-m font-medium',
      showBonusPrice && 'pt-2.5',
      !isVariantSelected && !isVariantNotAvailable && 'text-dark-brand',
      isVariantNotAvailable && 'text-dark-primary-strong',
      isVariantSelected && !isVariantNotAvailable && 'text-dark-primary-max',
    ),
  getDisplayPriceClass: (
    isVariantSelected: boolean,
    isLarge: boolean,
    isPriceDisplayed: boolean,
  ): string =>
    clsx(
      isLarge && 'flex justify-end text-m',
      isVariantSelected && 'font-bold text-dark-brand',
      !isPriceDisplayed && 'invisible',
      !isVariantSelected && 'text-dark-accent',
      !isLarge && 'text-m',
    ),
  getLinkClass: ({
    isLarge,
    isVariantNotAvailable,
    isVariantSelected,
    showBonusPrice,
    showDiscountedPrice,
  }: {
    isLarge: boolean
    isVariantNotAvailable: boolean
    isVariantSelected: boolean
    showBonusPrice: boolean
    showDiscountedPrice: boolean
  }): string =>
    clsx(
      'relative mb-2 block w-full rounded-lg border',
      isLarge ? 'px-5 pb-5 pt-7' : 'px-2.5 py-5',
      isVariantNotAvailable && 'bg-light-primary-high',
      isVariantSelected && isVariantNotAvailable && 'border-dark-brand',
      !isVariantSelected &&
        !isVariantNotAvailable &&
        'border-light-primary-low bg-light-primary-low',
      !isVariantSelected && isVariantNotAvailable && 'border-transparent',
      isVariantSelected &&
        !isVariantNotAvailable &&
        'border-transparent bg-light-brand-high',
      showBonusPrice && 'mt-6',
      showDiscountedPrice && 'mt-6 tablet:mt-4',
    ),
  getTileClass: (isLarge: boolean): string =>
    clsx(
      'inline-flex min-w-[132px] max-w-full shrink-0 flex-col tablet:min-w-0',
      isLarge && 'tablet:w-full desktop:w-[calc(50%-5px)]',
      !isLarge && 'text-center tablet:mr-0 tablet:w-[102px]',
    ),
}

export const ProductAttributeTileWithPrice: FC<
  ProductAttributeTileWithPriceProps
> = ({
  attributeDescription,
  candidateVariant,
  className,
  hasAnyBonuses = false,
  hasAnyDiscounts = false,
  isVariantSelected,
  onScrollIntoView,
  selectionEntry,
  size,
}) => {
  const intl = useIntl()

  const { currentPrescriptionType, isLarge, isVariantNotAvailable, prices } =
    useProductAttributeTileWithPriceData(size, candidateVariant)
  const globalConfig = useGlobalConfigContext()
  const { attributeSelectionControls } = useProductPageContext()
  const tileElementRef = useRef(null)

  const showBonusPrice =
    bonusEnabledPrescriptionTypes.includes(currentPrescriptionType) &&
    hasAnyBonuses
  const showDiscountedPrice =
    PrescriptionPrescriptionTypeEnum.NoPrescription ===
      currentPrescriptionType && hasAnyDiscounts

  useMobileScrollAttributeIntoViewOnMount({
    attributeElement: tileElementRef,
    isVariantSelected,
    onScrollIntoView,
  })

  return (
    <div
      className={clsx(classNames.getTileClass(isLarge), className)}
      ref={tileElementRef}
      title={selectionEntry.attribute.value}
    >
      <NextLink
        className={classNames.getLinkClass({
          isLarge,
          isVariantNotAvailable,
          isVariantSelected,
          showBonusPrice,
          showDiscountedPrice,
        })}
        data-qa-id="offer-attribute-price-tile"
        href={urlResolverGetPathForPimProduct(globalConfig, candidateVariant)}
        onClick={(): void => {
          attributeSelectionControls.handleAttributeSelect(selectionEntry)
        }}
        scroll={false}
        title={selectionEntry.attribute.value}
      >
        <div
          className={clsx(
            'absolute top-0 -translate-y-1/2',
            isLarge && 'tablet:left-auto tablet:translate-x-0',
          )}
        >
          <ProductDiscountBadge
            currentPrescriptionType={currentPrescriptionType}
            data-qa-id="offer-attribute-price-tile-prescription-badge"
            discount={prices.discount}
            isDiscountValid={prices.isDiscountValid}
            offerPrice={prices.displayPrice}
            showThirtyDaysPrice={prices.showThirtyDaysPrice}
          />
        </div>
        <ul
          className={clsx(
            'flex flex-col items-start text-left',
            isLarge &&
              'flex-wrap justify-between tablet:flex-row tablet:text-left',
          )}
        >
          <li
            className={classNames.getAttributeDescriptionClass({
              isVariantNotAvailable,
              isVariantSelected,
              showBonusPrice,
            })}
          >
            {attributeDescription}
          </li>
          <li
            // Prevent google displaying the price on search for prescription products
            {...productAccessorGetDataNoSnippetAttributeForInsurancePrescription(
              currentPrescriptionType,
            )}
            className={classNames.getDisplayPriceClass(
              isVariantSelected,
              isLarge,
              Boolean(prices.displayPrice),
            )}
          >
            {priceGetFormattedPrice(prices.displayPrice?.price ?? 0, intl)}
          </li>
          <ProductAttributeTileStrikeThroughPrice
            currentPrescriptionType={currentPrescriptionType}
            isLarge={isLarge}
            prices={prices}
          />
        </ul>
      </NextLink>
      {prices.formattedUnitPrice ? (
        <ProductFormattedUnitPrice
          classNames="tablet:w-[100px] text-xs text-center mb-2.5"
          formattedUnitPrice={prices.formattedUnitPrice}
        />
      ) : null}
    </div>
  )
}
